import { ArrowRight, CalendarCheck, Clock3 } from "lucide-react";
import { articlePath, readingTimeMinutes, relatedArticles } from "../content";
import { headingId } from "../domain/richText";
import { articleCrumbs, faqId } from "../seo";
import { FAQ_PATH, type Article } from "../shared";
import { ArticleBody, Inline } from "./ArticleBody";
import { Breadcrumbs, ReadingShell } from "./ReadingShell";

/**
 * One article, rendered the same way in the browser and in the prerendered
 * document a crawler is served.
 *
 * Three things on this page exist twice, once for a reader and once inside the
 * article's structured data, and both copies are built from the same value:
 * the breadcrumb trail, the reading time, and every question at the foot. That
 * is the rule the whole SEO layer runs on. Structured data may only state what
 * the page shows, so nothing here is allowed to be assembled separately.
 */

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function ArticleView({ article }: { article: Article }) {
  const minutes = readingTimeMinutes(article);
  const related = relatedArticles(article);
  const headings = article.body.filter((block) => block.kind === "heading");

  return (
    <ReadingShell>
      <main className="article">
        <Breadcrumbs crumbs={articleCrumbs(article)} />

        <header className="article__head">
          <p className="tag tag--sun">
            <span aria-hidden="true">{article.emoji}</span> {article.category}
          </p>
          <h1>{article.title}</h1>
          <p className="article__lede">
            <Inline text={article.lede} />
          </p>
          <dl className="article__meta">
            <div>
              <dt>
                <Clock3 size={15} aria-hidden="true" /> Reading time
              </dt>
              <dd>{minutes} min</dd>
            </div>
            <div>
              <dt>
                <CalendarCheck size={15} aria-hidden="true" /> Last updated
              </dt>
              <dd>
                <time dateTime={article.updated}>{formatDate(article.updated)}</time>
              </dd>
            </div>
          </dl>
        </header>

        {headings.length > 2 ? (
          <nav className="article__contents" aria-label="On this page">
            <h2>On this page</h2>
            <ol>
              {headings.map((heading) => (
                <li key={heading.text}>
                  <a href={`#${headingId(heading.text)}`}>
                    <Inline text={heading.text} />
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}

        <ArticleBody blocks={article.body} />

        {article.faqs.length > 0 ? (
          <section className="article__faqs" aria-labelledby="article-faqs">
            <h2 id="article-faqs">Questions people ask</h2>
            <dl>
              {article.faqs.map((faq) => (
                <div id={faqId(faq.question)} key={faq.question}>
                  <dt>{faq.question}</dt>
                  <dd>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section className="article__cta">
          <h2>{article.ctaLabel}</h2>
          <p>
            Two languages, a word count, and one sheet a day. No account, no
            card, and nothing to cancel.
          </p>
          <a className="btn btn--primary btn--lg" href={article.ctaHref}>
            <span className="btn__icon" aria-hidden="true">
              <ArrowRight size={19} />
            </span>
            <span>{article.ctaLabel}</span>
          </a>
        </section>

        <section className="article__sources" aria-labelledby="article-sources">
          <h2 id="article-sources">Sources</h2>
          <p className="article__sources-note">
            Every source below was opened on the date shown. A claim about a
            language, or about how people remember one, ages; the date is how
            you tell whether this page has kept up.
          </p>
          <ol>
            {article.sources.map((source) => (
              <li key={source.id}>
                <a href={source.url} rel="noreferrer nofollow" target="_blank">
                  {source.title}
                </a>
                <span className="article__source-meta">
                  {source.authority}, checked{" "}
                  <time dateTime={source.lastChecked}>
                    {formatDate(source.lastChecked)}
                  </time>
                </span>
              </li>
            ))}
          </ol>
        </section>

        {related.length > 0 ? (
          <section className="article__related" aria-labelledby="article-related">
            <h2 id="article-related">Read next</h2>
            <ul>
              {related.map((next) => (
                <li key={next.slug}>
                  <a href={articlePath(next.slug)}>
                    <span className="article__related-emoji" aria-hidden="true">
                      {next.emoji}
                    </span>
                    <span>
                      <strong>{next.title}</strong>
                      <small>{next.description}</small>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="article__related-more">
              Still deciding what to do first? <a href={FAQ_PATH}>The questions
              page</a> covers how the sheets work, what leaves your device, and
              what this costs.
            </p>
          </section>
        ) : null}
      </main>
    </ReadingShell>
  );
}
